import { LineageBootstrap } from "@/components/admin/LineageBootstrap";
import { NetworkLogBootstrap } from "@/components/admin/NetworkLogBootstrap";
import { FlagsV2Wrapper } from "@/components/context-providers/FlagsWrapper";
import { ScopedEnvironmentFlagsProvider } from "@/components/context-providers/ScopedEnvironmentFlagsProvider";
import { RouteAndTeamInfoProvider } from "@/components/context-providers/RouteAndTeamInfoProvider";
import { TeamProvider } from "@/components/context-providers/TeamProvider";
import { TeamRefreshingWrapper } from "@/components/context-providers/TeamRefreshingWrapper";
import { ImpersonationWrapper } from "@/components/layout/ImpersonationWrapper";
import { parseEnvironmentIdFromUrl } from "@/utils/connect-query/environment-id";
import { DashboardFixtureCaptureBootstrap } from "@/utils/connect-query/fixtures/DashboardFixtureCaptureBootstrap";
import { makeGrpcQueryPersistenceScope } from "@/utils/connect-query/query-cache-persistence/persisted-grpc-query-scope";
import { usePreloadPersistedGrpcQueryCache } from "@/utils/connect-query/query-cache-persistence/preload-persisted-grpc-query-cache";
import { PersistedGrpcQueryRestoringProvider } from "@/utils/connect-query/query-cache-persistence/use-persisted-grpc-query";
import { useRouteInfo } from "@/utils/hooks/url/use-route-info";
import { useNetworkingClients } from "@/utils/hooks/use-networking-clients";
import { useResetStoreOnEnvironmentChange } from "@/utils/hooks/use-reset-cache-environment";
import { PylonIntegration } from "@/utils/pylon-init";
import { TransportProvider } from "@connectrpc/connect-query";
import { QueryClientProvider } from "@tanstack/react-query";
import { useSession } from "next-auth/react";
import { PropsWithChildren, useMemo } from "react";

export interface NetworkingWrapperProps extends PropsWithChildren {
  grpcApiHost: string;
}

/**
 * This is designed to be a singleton, only used once in the root of the project in _app.tsx.
 *
 * It provides the grpc transport and query client, as well as other networking-related context providers
 * that are used to wrap pages in the application that require a networking client
 * but do not necessarily require authentication or team membership.
 */
export const UnauthenticatedNetworkingWrapper: React.FC<NetworkingWrapperProps> = ({
  children,
  grpcApiHost,
}) => {
  const { grpcTransport, grpcQueryClient } = useNetworkingClients(grpcApiHost ?? "");

  return (
    <TransportProvider transport={grpcTransport}>
      <QueryClientProvider client={grpcQueryClient}>{children}</QueryClientProvider>
    </TransportProvider>
  );
};

/**
 * This is designed to be a singleton, only used once in the root of the project in _app.tsx.
 *
 * It provides the grpc transport and query client, as well as other networking-related context providers
 * that are used to wrap the internal dashboard pages of the application that require
 * a networking client as well as authentication and team membership.
 */
export const NetworkingWrapper: React.FC<NetworkingWrapperProps> = ({ children, grpcApiHost }) => {
  const { grpcTransport, grpcQueryClient } = useNetworkingClients(grpcApiHost);
  const session = useSession();
  const { environmentId } = useRouteInfo();

  // disabling the eslint rule that prevents the use of this hook; it is intended to be used as a singleton in this root-level wrapper component.
  // eslint-disable-next-line eslint-js/no-restricted-syntax
  useResetStoreOnEnvironmentChange(grpcQueryClient);

  const environmentIdForPersistence = useMemo(() => {
    if (environmentId != null) {
      return environmentId;
    }

    return typeof window !== "undefined" ? parseEnvironmentIdFromUrl(window.location.href) : null;
  }, [environmentId]);

  const persistenceScope = useMemo(
    () =>
      makeGrpcQueryPersistenceScope({
        grpcApiHost,
        userId: session.data?.userId,
        teamId: session.data?.teamId,
        environmentId: environmentIdForPersistence,
      }),
    [environmentIdForPersistence, grpcApiHost, session.data?.teamId, session.data?.userId],
  );

  // disabling the eslint rule that prevents the use of this hook; it is intended to be used as a singleton in this root-level wrapper component.
  // eslint-disable-next-line eslint-js/no-restricted-syntax
  const isRestoringGrpcQueryCache = usePreloadPersistedGrpcQueryCache(grpcQueryClient, {
    scope: persistenceScope,
    // Only hold the restoring gate for the *initial* session load (no session data yet).
    // next-auth's update() flips status to "loading" while keeping session.data; re-entering
    // the restoring state then would needlessly disable the persisted-endpoint queries again.
    waitForScope: session.status === "loading" && session.data == null,
  });

  // explicitly disabling the eslint rules that prevent the use of these wrapper components;
  // they are intended to be used as singletons in this root-level wrapper component.
  return (
    <TransportProvider transport={grpcTransport}>
      <QueryClientProvider client={grpcQueryClient}>
        <DashboardFixtureCaptureBootstrap />
        <NetworkLogBootstrap />
        <LineageBootstrap />
        {/* Gates only PERSISTED_GRPC_QUERY_ENDPOINTS (see usePersistedGrpcQuery); every other
            query fetches without waiting for the IndexedDB restore. */}
        <PersistedGrpcQueryRestoringProvider value={isRestoringGrpcQueryCache}>
          {/* eslint-disable-next-line eslint-js/no-restricted-syntax */}
          <FlagsV2Wrapper>
            {/* eslint-disable-next-line eslint-js/no-restricted-syntax */}
            <TeamRefreshingWrapper>
              {/* eslint-disable-next-line eslint-js/no-restricted-syntax */}
              <TeamProvider>
                <PylonIntegration />
                {/* eslint-disable-next-line eslint-js/no-restricted-syntax */}
                <RouteAndTeamInfoProvider>
                  {/* eslint-disable-next-line eslint-js/no-restricted-syntax */}
                  <ScopedEnvironmentFlagsProvider>
                    {/* eslint-disable-next-line eslint-js/no-restricted-syntax */}
                    <ImpersonationWrapper>{children}</ImpersonationWrapper>
                  </ScopedEnvironmentFlagsProvider>
                </RouteAndTeamInfoProvider>
              </TeamProvider>
            </TeamRefreshingWrapper>
          </FlagsV2Wrapper>
        </PersistedGrpcQueryRestoringProvider>
      </QueryClientProvider>
    </TransportProvider>
  );
};
